//
// AHT Weight Management — Tirzepatide (Mounjaro®) Full Re-screen Pathway
// Source: AHT_Mounjaro_Clinical_Pathway__1_.docx v1.4
//
// Triggered when a continuation consultation identifies a change since the last
// consultation. Because there are no prior answers to confirm, the exclusion
// items that the initial pathway confirms via its confirmation card are asked
// here one at a time as individual yes/no questions (positive phrasing — a
// "yes" means the risk is present). The downstream PGD checks, interaction
// checks, cautions, attestation, and outcomes are the shared single source of
// truth imported from _shared/tirzepatide-screening-downstream.
//
// QUANTITIES ARE PRE-VERIFIED LOOKUPS. DO NOT CALCULATE.

import type { Pathway } from "../../types/pathway.types";
import {
  PGD_FRESH_CHECKS,
  Q5_CAUTIONS,
  Q6_ATTEST,
  OUT_NO_F2F,
  OUT_CANNOT_SUPPLY,
  OUT_SUPPLY,
  tirzepatideScreeningDownstreamNodes,
  tirzepatideScreeningOutcomes,
} from "./_shared/tirzepatide-screening-downstream";

// ─────────────────────────────────────────────────────────────────────────────
// Node IDs
// ─────────────────────────────────────────────────────────────────────────────

const Q1_F2F      = "tzp-rescr-q1-f2f";
const EX1_THYROID = "tzp-rescr-ex1-thyroid-men2";
const EX2_PANCR   = "tzp-rescr-ex2-pancreatitis";
const EX3_DIABETES = "tzp-rescr-ex3-diabetes";
const EX4_RENAL_HEP = "tzp-rescr-ex4-renal-hepatic";
const EX5_GI      = "tzp-rescr-ex5-gi-gastroparesis";
const EX6_PREG    = "tzp-rescr-ex6-pregnancy";
const EX7_RETINO  = "tzp-rescr-ex7-retinopathy";
const DOSE        = "tzp-rescr-dose-select";
const ATTEST      = "tzp-rescr-dose-confirm";

// ─────────────────────────────────────────────────────────────────────────────
// Pathway
// ─────────────────────────────────────────────────────────────────────────────

export const ahtWtTirzepatideRescreenPathway: Pathway = {
  id: "aht-wt-tirzepatide-rescreen",
  name: "Tirzepatide (Mounjaro®) — Full Re-screen",
  population: "Adults aged 18–84 attending face-to-face",
  pgdRefs: ["pgd-aht-wt-tirzepatide"],
  demographicConfiguration: { bannerFields: [] },
  entryNode: Q1_F2F,

  nodes: [
    // ─────────────────────────────────────────────────────────────────────────
    // Step 1 — Face-to-face confirmation (same as initial)
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: Q1_F2F,
      type: "yes_no",
      text: "Is the patient attending face-to-face at AHT registered premises?",
      hint: "Face-to-face consultation is required for all supplies under this PGD (initial and subsequent). No virtual or online consultations permitted.",
      yes: EX1_THYROID,
      no: { outcomeId: OUT_NO_F2F },
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Step 2 — Exclusion questions (one per confirmation-card item, in order).
    // Positive phrasing: "yes" = risk present → cannot supply.
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: EX1_THYROID,
      type: "yes_no",
      text: "Personal or family history of thyroid cancer or MEN2?",
      yes: { outcomeId: OUT_CANNOT_SUPPLY },
      no: EX2_PANCR,
    },
    {
      id: EX2_PANCR,
      type: "yes_no",
      text: "History of pancreatitis (acute or chronic)?",
      yes: { outcomeId: OUT_CANNOT_SUPPLY },
      no: EX3_DIABETES,
    },
    {
      id: EX3_DIABETES,
      type: "yes_no",
      text: "Type 1 or Type 3c diabetes mellitus?",
      yes: { outcomeId: OUT_CANNOT_SUPPLY },
      no: EX4_RENAL_HEP,
    },
    {
      id: EX4_RENAL_HEP,
      type: "yes_no",
      text: "Severe renal impairment (CrCl <15 mL/min) or severe hepatic impairment?",
      yes: { outcomeId: OUT_CANNOT_SUPPLY },
      no: EX5_GI,
    },
    {
      id: EX5_GI,
      type: "yes_no",
      text: "Severe or unstable gastrointestinal disease including gastroparesis?",
      yes: { outcomeId: OUT_CANNOT_SUPPLY },
      no: EX6_PREG,
    },
    {
      id: EX6_PREG,
      type: "yes_no",
      text: "Pregnant, breastfeeding, or planning pregnancy?",
      yes: { outcomeId: OUT_CANNOT_SUPPLY },
      no: EX7_RETINO,
      autoAnswer: { when: { patientIsFemale: false }, value: false },
    },
    {
      id: EX7_RETINO,
      type: "yes_no",
      text: "Diabetic retinopathy or macular oedema?",
      yes: { outcomeId: OUT_CANNOT_SUPPLY },
      no: PGD_FRESH_CHECKS,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Steps 2b–4 — shared screening downstream (PGD fresh checks, interaction
    // checks, cautions review). The shared fixed-dose attestation is dropped and
    // the cautions node is re-routed to the dose selector below, because an
    // established patient is re-escalated to their dose on full re-screen.
    // ─────────────────────────────────────────────────────────────────────────
    ...tirzepatideScreeningDownstreamNodes
      .filter((node) => node.id !== Q6_ATTEST)
      .map((node) => (node.id === Q5_CAUTIONS ? { ...node, next: DOSE } : node)),

    // ─────────────────────────────────────────────────────────────────────────
    // Step 5 — Dose selection (pharmacist sets the re-escalated dose).
    // Options mirrored EXACTLY from tzp-trans-dose-select. KEEP IN SYNC with
    // aht-wt-tirzepatide-transfer.pathway.ts and the drug's source doses.
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: DOSE,
      type: "single_select",
      text: "What dose should be supplied?",
      options: [
        { id: "tzp-2_5mg",  label: "2.5 mg SC once weekly" },
        { id: "tzp-5mg",    label: "5 mg SC once weekly" },
        { id: "tzp-7_5mg",  label: "7.5 mg SC once weekly" },
        { id: "tzp-10mg",   label: "10 mg SC once weekly" },
        { id: "tzp-12_5mg", label: "12.5 mg SC once weekly" },
        { id: "tzp-15mg",   label: "15 mg SC once weekly" },
      ],
      routing: {
        "tzp-2_5mg":  ATTEST,
        "tzp-5mg":    ATTEST,
        "tzp-7_5mg":  ATTEST,
        "tzp-10mg":   ATTEST,
        "tzp-12_5mg": ATTEST,
        "tzp-15mg":   ATTEST,
      },
      storesTo: "selectedDoseStep",
      required: true,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Step 6 — Supply attestation (selected dose). Dose-agnostic wording; the
    // selected dose is captured in selectedDoseStep, mirroring the transfer
    // pathway (no {{selected_dose}} interpolation is wired in the engine).
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: ATTEST,
      type: "attestation",
      text: "Step 5 — Confirm supply of Mounjaro® (tirzepatide) at the selected dose",
      attestationStatement:
        "I confirm that I have:\n" +
        "• Supplied 1 × Mounjaro® KwikPen at the selected dose (4 doses)\n" +
        "• Checked pen integrity and cold chain before supply\n" +
        "• Confirmed storage: 2–8°C before first use; after first use ≤30°C or refrigerated up to 30 days\n" +
        "• Counselled the patient on subcutaneous injection technique and injection site rotation\n" +
        "• Counselled the patient on missed dose guidance\n" +
        "• Advised the patient to inform their GP of initiation\n" +
        "• Provided patient information leaflet\n" +
        "• Recorded batch number and expiry date",
      conclusions: {
        optionA: { label: "Confirm and supply", next: { outcomeId: OUT_SUPPLY } },
        optionB: { label: "Do not supply",      next: { outcomeId: OUT_CANNOT_SUPPLY } },
      },
    },
  ],

  outcomes: {
    ...tirzepatideScreeningOutcomes,
    [OUT_SUPPLY]: {
      ...tirzepatideScreeningOutcomes[OUT_SUPPLY]!,
      title: "Supply complete — Mounjaro® (tirzepatide), selected dose",
    },
  },
};
